import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'react-qr-code';
import {
  User,
  Settings,
  QrCode,
  LogOut,
  Menu,
  X,
  Mail,
  GraduationCap,
  Calendar,
  Code,
  Users,
  Award,
  Edit3,
  Save,
  Eye,
  BarChart3
} from 'lucide-react';
import useravatar from '../components/assets/user.png';

function NewDashboard() {
  const navigate = useNavigate();

  // State management
  const [activeTab, setActiveTab] = useState("dashboard");
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationError, setNotificationError] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    branch: '',
    year: '',
    skills: '',
    nsutEmail: '',
    about: '',
    isMember: false,
    department: '',
    role: '',
    memberId: ''
  });

  // Load user profile on mount
  useEffect(() => {
    fetchUserProfile();
  }, []);

  // Update form data when user profile changes
  useEffect(() => {
    if (userProfile) {
      setFormData({
        name: userProfile.name || '',
        branch: userProfile.branch || '',
        year: userProfile.year || '',
        skills: Array.isArray(userProfile.skills)
          ? userProfile.skills.join(', ')
          : userProfile.skills || '',
        nsutEmail: userProfile.nsutEmail || '',
        about: userProfile.about || '',
        isMember: userProfile.isMember || false,
        department: userProfile.department || '',
        role: userProfile.role || '',
        memberId: userProfile.memberId || ''
      });
    }
  }, [userProfile]);

  // Fetch user profile from backend
  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('jwtToken');
      if (!token) {
        setError('Authentication token not found. Please login again.');
        setLoading(false);
        return;
      }
      const response = await fetch('https://aeronauts-nsut.onrender.com/api/profile/user', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
      });
   
      if (!response.ok) {
        throw new Error('Failed to fetch profile data');
      }

      const profileData = await response.json();
      setUserProfile(profileData);

      // Set initial tab based on profile completeness
      if (profileData && Object.keys(profileData).length > 0 && profileData.name) {
        setActiveTab("dashboard");
      } else {
        setActiveTab("profile");
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError('Error loading profile data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle profile update
  const handleUpdateProfile = async () => {
    const processedSkills = Array.isArray(formData.skills)
      ? formData.skills
      : typeof formData.skills === 'string'
        ? formData.skills.split(',').map(skill => skill.trim()).filter(skill => skill !== '')
        : [];

    const updatedProfile = {
      ...formData,
      skills: processedSkills
    };

    try {
      const token = localStorage.getItem('jwtToken');

      if (!token) {
        setNotificationError(true);
        setShowNotification(true);
        throw new Error('No authentication token found. Please login again.');
      }

      // Form validation
      if (!formData.name || !formData.nsutEmail || !formData.branch || !formData.year) {
        setNotificationError(true);
        setShowNotification(true);
        throw new Error('Please fill in all required fields');
      }

      // Validate email format
      if (!formData.nsutEmail.endsWith('@nsut.ac.in')) {
        setNotificationError(true);
        setShowNotification(true);
        throw new Error('Please use a valid NSUT email address');
      }

      const response = await fetch('https://aeronauts-nsut.onrender.com/api/profile/update', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedProfile),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update profile');
      }

      const data = await response.json();
      setUserProfile(data.profile || data);
      setNotificationError(false);
      setShowNotification(true);
      setActiveTab("dashboard");

      setTimeout(() => {
        setShowNotification(false);
      }, 3000);

    } catch (error) {
      console.error('Error updating profile:', error);
      setNotificationError(true);
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 3000);
    }
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  // Generate QR code data
  const getQRCodeData = () => {
    return JSON.stringify(userProfile || formData);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 text-white relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 opacity-20">
        <div className="w-full h-full bg-gradient-to-br from-blue-500/10 to-purple-500/10"></div>
      </div>

      {/* Notification */}
      {showNotification && (
        <div className={`fixed top-6 right-6 p-4 rounded-xl backdrop-blur-md border z-50 shadow-2xl ${notificationError
            ? 'bg-red-500/20 border-red-500/30 text-red-100'
            : 'bg-green-500/20 border-green-500/30 text-green-100'
          }`}>
          <div className="flex items-center space-x-2">
            {notificationError ? (
              <X className="w-5 h-5" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            <span>{notificationError ? 'Failed to update profile. Please try again.' : 'Profile updated successfully!'}</span>
          </div>
        </div>
      )}

      {/* Mobile Sidebar Toggle */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 bg-white/10 backdrop-blur-md border border-white/20 text-white p-2.5 sm:p-3 rounded-xl hover:bg-white/20 transition-all duration-300 shadow-lg"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? <X className="w-5 h-5 sm:w-6 sm:h-6" /> : <Menu className="w-5 h-5 sm:w-6 sm:h-6" />}
      </button>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex min-h-screen">
        {/* Sidebar */}
        <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-40 w-72 sm:w-80 bg-black/20 backdrop-blur-xl border-r border-white/10 transform transition-all duration-300 ease-in-out`}>

          {/* User Profile Header */}
          <div className="p-4 sm:p-6 border-b border-white/10">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="relative flex-shrink-0">
                <img
                  src={userProfile?.avatarURL || useravatar}
                  alt="User Avatar"
                  className="w-12 h-12 sm:w-16 sm:h-16 rounded-full border-2 border-blue-400/50 shadow-lg"
                />
                <div className="absolute -bottom-0.5 -right-0.5 sm:-bottom-1 sm:-right-1 w-4 h-4 sm:w-5 sm:h-5 bg-green-500 rounded-full border-2 border-black/20"></div>
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-base sm:text-lg font-semibold text-white truncate">{userProfile?.name || "User Name"}</h3>
                <p className="text-blue-200/70 text-xs sm:text-sm flex items-center">
                  <Mail className="w-3 h-3 mr-1 flex-shrink-0" />
                  <span className="truncate">{userProfile?.nsutEmail || "Email"}</span>
                </p>
              </div>
            </div>
          </div>

          {/* Navigation Menu */}
          <nav className="p-3 sm:p-4 space-y-2 sm:space-y-3 flex-1">
            <button
              onClick={() => { setActiveTab("dashboard"); setSidebarOpen(false); }}
              className={`w-full flex items-center space-x-2 sm:space-x-3 p-3 sm:p-4 rounded-xl transition-all duration-300 text-sm sm:text-base ${activeTab === "dashboard"
                  ? 'bg-blue-500/30 border border-blue-400/50 text-white shadow-lg backdrop-blur-sm'
                  : 'text-blue-100/80 hover:bg-white/10 hover:text-white border border-transparent hover:border-white/20'
                }`}
            >
              <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              <span className="font-medium">Dashboard</span>
            </button>
            <button
              onClick={() => { setActiveTab("profile"); setSidebarOpen(false); }}
              className={`w-full flex items-center space-x-2 sm:space-x-3 p-3 sm:p-4 rounded-xl transition-all duration-300 text-sm sm:text-base ${activeTab === "profile"
                  ? 'bg-blue-500/30 border border-blue-400/50 text-white shadow-lg backdrop-blur-sm'
                  : 'text-blue-100/80 hover:bg-white/10 hover:text-white border border-transparent hover:border-white/20'
                }`}
            >
              <Edit3 className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              <span className="font-medium">Update Profile</span>
            </button>
            <button
              onClick={() => { setActiveTab("qr"); setSidebarOpen(false); }}
              className={`w-full flex items-center space-x-2 sm:space-x-3 p-3 sm:p-4 rounded-xl transition-all duration-300 text-sm sm:text-base ${activeTab === "qr"
                  ? 'bg-blue-500/30 border border-blue-400/50 text-white shadow-lg backdrop-blur-sm'
                  : 'text-blue-100/80 hover:bg-white/10 hover:text-white border border-transparent hover:border-white/20'
                }`}
            >
              <QrCode className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              <span className="font-medium">QR Code</span>
            </button>
            <div className="pt-4">
              <button
                onClick={handleLogout}
                className="w-full flex items-center space-x-2 sm:space-x-3 p-3 sm:p-4 rounded-xl text-red-300 hover:bg-red-500/20 hover:text-red-200 border border-transparent hover:border-red-400/30 transition-all duration-300 text-sm sm:text-base"
              >
                <LogOut className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                <span className="font-medium">Logout</span>
              </button>
            </div>
          </nav>

          {/* Footer */}
          <div className="p-6 border-t border-white/10">
            <p className="text-center text-blue-200/50 text-sm">© {new Date().getFullYear()} Aeronauts</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 lg:ml-0 p-4 sm:p-6 relative pt-20 lg:pt-6">
          {/* Dashboard View */}
          {activeTab === "dashboard" && (
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center space-x-2 sm:space-x-3 mb-6 sm:mb-8">
                <BarChart3 className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400" />
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  Dashboard
                </h1>
              </div>

              {error ? (
                <div className="bg-red-500/20 backdrop-blur-md border border-red-500/30 text-red-100 p-6 rounded-xl shadow-2xl">
                  <div className="flex items-center space-x-3">
                    <X className="w-6 h-6" />
                    <span>{error}</span>
                  </div>
                </div>
              ) : !userProfile || Object.keys(userProfile).length === 0 ? (
                <div className="bg-yellow-500/20 backdrop-blur-md border border-yellow-500/30 text-yellow-100 p-6 rounded-xl shadow-2xl">
                  <div className="flex items-center space-x-3">
                    <User className="w-6 h-6" />
                    <span>No profile data available. Please update your profile.</span>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {/* Profile Card */}
                  <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4 sm:p-6 lg:p-8 col-span-full lg:col-span-2 shadow-2xl">
                    <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6 text-center sm:text-left">
                      <div className="relative flex-shrink-0">
                        <img
                          src={userProfile.avatarURL || useravatar}
                          alt="Profile"
                          className="w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28 rounded-full border-4 border-blue-400/50 shadow-xl"
                        />
                        <div className="absolute -bottom-1 -right-1 sm:-bottom-2 sm:-right-2 bg-green-500 w-6 h-6 sm:w-8 sm:h-8 rounded-full border-2 sm:border-4 border-white/20 flex items-center justify-center">
                          <Eye className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-2 break-words">{userProfile.name}</h2>
                        <p className="text-blue-300 flex items-center justify-center sm:justify-start mb-1 text-sm sm:text-base">
                          <Mail className="w-3 h-3 sm:w-4 sm:h-4 mr-2 flex-shrink-0" />
                          <span className="truncate">{userProfile.nsutEmail}</span>
                        </p>
                        <p className="text-purple-300 flex items-center justify-center sm:justify-start text-sm sm:text-base">
                          <GraduationCap className="w-3 h-3 sm:w-4 sm:h-4 mr-2 flex-shrink-0" />
                          <span>{userProfile.branch} • Year {userProfile.year}</span>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4 sm:p-6 shadow-2xl">
                    <div className="flex items-center space-x-2 sm:space-x-3 mb-4">
                      <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400" />
                      <h3 className="text-base sm:text-lg font-semibold text-white">Quick Stats</h3>
                    </div>
                    <div className="space-y-3 sm:space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-blue-200/80 flex items-center text-sm sm:text-base">
                          <Code className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                          Skills:
                        </span>
                        <span className="bg-blue-500/30 px-2 sm:px-3 py-1 rounded-full text-white font-medium text-sm sm:text-base">
                          {Array.isArray(userProfile.skills) ? userProfile.skills.length : 0}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-blue-200/80 flex items-center text-sm sm:text-base">
                          <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                          Member:
                        </span>
                        <span className={`px-2 sm:px-3 py-1 rounded-full font-medium text-sm sm:text-base ${userProfile.isMember
                            ? 'bg-green-500/30 text-green-200'
                            : 'bg-red-500/30 text-red-200'
                          }`}>
                          {userProfile.isMember ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Academic Info */}
                  <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4 sm:p-6 shadow-2xl">
                    <div className="flex items-center space-x-2 sm:space-x-3 mb-4">
                      <GraduationCap className="w-5 h-5 sm:w-6 sm:h-6 text-purple-400" />
                      <h3 className="text-base sm:text-lg font-semibold text-white">Academic Info</h3>
                    </div>
                    <div className="space-y-2 sm:space-y-3">
                      <p className="flex flex-col sm:flex-row sm:items-center text-sm sm:text-base">
                        <span className="text-purple-200/80 sm:w-16 font-medium">Branch:</span>
                        <span className="text-white font-medium">{userProfile.branch || "Not specified"}</span>
                      </p>
                      <p className="flex flex-col sm:flex-row sm:items-center text-sm sm:text-base">
                        <span className="text-purple-200/80 sm:w-16 font-medium flex items-center">
                          <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 text-purple-300" />
                          Year:
                        </span>
                        <span className="text-white font-medium">{userProfile.year || "Not specified"}</span>
                      </p>
                    </div>
                  </div>

                  {/* Skills */}
                  {userProfile.skills && (
                    <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4 sm:p-6 shadow-2xl">
                      <div className="flex items-center space-x-2 sm:space-x-3 mb-4">
                        <Code className="w-5 h-5 sm:w-6 sm:h-6 text-green-400" />
                        <h3 className="text-base sm:text-lg font-semibold text-white">Skills</h3>
                      </div>
                      <div className="flex flex-wrap gap-1.5 sm:gap-2">
                        {(Array.isArray(userProfile.skills) ? userProfile.skills :
                          typeof userProfile.skills === 'string' ? userProfile.skills.split(',') : []
                        ).map((skill, index) => (
                          <span key={index} className="bg-gradient-to-r from-blue-500/30 to-purple-500/30 backdrop-blur-sm border border-blue-400/30 text-white px-2 sm:px-3 lg:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-medium hover:from-blue-500/40 hover:to-purple-500/40 transition-all duration-300">
                            {skill.trim()}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* About */}
                  {userProfile.about && (
                    <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4 sm:p-6 shadow-2xl">
                      <div className="flex items-center space-x-2 sm:space-x-3 mb-4">
                        <User className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400" />
                        <h3 className="text-base sm:text-lg font-semibold text-white">About</h3>
                      </div>
                      <p className="text-blue-100/90 leading-relaxed text-sm sm:text-base">{userProfile.about}</p>
                    </div>
                  )}

                  {/* Society Info */}
                  {userProfile.isMember && (
                    <div className="bg-gradient-to-br from-blue-500/20 to-purple-600/20 backdrop-blur-xl border border-blue-400/30 rounded-2xl p-4 sm:p-6 shadow-2xl">
                      <div className="flex items-center space-x-2 sm:space-x-3 mb-4">
                        <Award className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400" />
                        <h3 className="text-base sm:text-lg font-semibold text-white">Aeronauts Society</h3>
                      </div>
                      <div className="space-y-2 sm:space-y-3">
                        <p className="flex flex-col sm:flex-row sm:items-center text-sm sm:text-base">
                          <span className="text-blue-200/80 sm:w-24 font-medium">Department:</span>
                          <span className="text-white font-medium">{userProfile.department || "Not specified"}</span>
                        </p>
                        <p className="flex flex-col sm:flex-row sm:items-center text-sm sm:text-base">
                          <span className="text-blue-200/80 sm:w-24 font-medium">Role:</span>
                          <span className="text-white font-medium">{userProfile.role || "Not specified"}</span>
                        </p>
                        <p className="flex flex-col sm:flex-row sm:items-center text-sm sm:text-base">
                          <span className="text-blue-200/80 sm:w-24 font-medium">Member ID:</span>
                          <span className="text-white font-medium">{userProfile.memberId || "Not specified"}</span>
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Profile Update View */}
          {activeTab === "profile" && (
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center space-x-2 sm:space-x-3 mb-6 sm:mb-8">
                <Edit3 className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400" />
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  Update Profile
                </h1>
              </div>

              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4 sm:p-6 lg:p-8 shadow-2xl">
                {/* Avatar */}
                <div className="flex justify-center mb-6 sm:mb-8">
                  <div className="relative">
                    <img
                      src={userProfile?.avatarURL || useravatar}
                      alt="Profile Avatar"
                      className="w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28 rounded-full border-4 border-blue-400/50 shadow-xl"
                    />
                    <div className="absolute -bottom-1 -right-1 sm:-bottom-2 sm:-right-2 bg-blue-500 w-6 h-6 sm:w-8 sm:h-8 rounded-full border-2 sm:border-4 border-white/20 flex items-center justify-center">
                      <Settings className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                    </div>
                  </div>
                </div>

                {/* Form */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-3 text-blue-200 flex items-center">
                      <User className="w-4 h-4 mr-2" />
                      Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="w-full p-3 sm:p-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl focus:border-blue-400 focus:outline-none text-white placeholder-blue-200/50 transition-all duration-300 text-sm sm:text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-3 text-blue-200 flex items-center">
                      <Mail className="w-4 h-4 mr-2" />
                      NSUT Email *
                    </label>
                    <input
                      type="email"
                      value={formData.nsutEmail}
                      onChange={(e) => handleInputChange('nsutEmail', e.target.value)}
                      placeholder="example@nsut.ac.in"
                      className="w-full p-3 sm:p-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl focus:border-blue-400 focus:outline-none text-white placeholder-blue-200/50 transition-all duration-300 text-sm sm:text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-3 text-blue-200 flex items-center">
                      <GraduationCap className="w-4 h-4 mr-2" />
                      Branch *
                    </label>
                    <input
                      type="text"
                      value={formData.branch}
                      onChange={(e) => handleInputChange('branch', e.target.value)}
                      className="w-full p-3 sm:p-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl focus:border-blue-400 focus:outline-none text-white placeholder-blue-200/50 transition-all duration-300 text-sm sm:text-base"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-3 text-blue-200 flex items-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      Year *
                    </label>
                    <input
                      type="text"
                      value={formData.year}
                      onChange={(e) => handleInputChange('year', e.target.value)}
                      className="w-full p-3 sm:p-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl focus:border-blue-400 focus:outline-none text-white placeholder-blue-200/50 transition-all duration-300 text-sm sm:text-base"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-3 text-blue-200 flex items-center">
                      <Code className="w-4 h-4 mr-2" />
                      Skills (comma separated)
                    </label>
                    <input
                      type="text"
                      value={formData.skills}
                      onChange={(e) => handleInputChange('skills', e.target.value)}
                      placeholder="React, JavaScript, Python..."
                      className="w-full p-3 sm:p-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl focus:border-blue-400 focus:outline-none text-white placeholder-blue-200/50 transition-all duration-300 text-sm sm:text-base"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-3 text-blue-200 flex items-center">
                      <Edit3 className="w-4 h-4 mr-2" />
                      About (max 100 words)
                    </label>
                    <textarea
                      value={formData.about}
                      onChange={(e) => handleInputChange('about', e.target.value)}
                      maxLength={400}
                      rows={4}
                      placeholder="Brief description about yourself..."
                      className="w-full p-3 sm:p-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl focus:border-blue-400 focus:outline-none text-white placeholder-blue-200/50 resize-vertical transition-all duration-300 text-sm sm:text-base"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="flex items-center space-x-3 p-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl cursor-pointer hover:bg-white/10 transition-all duration-300">
                      <input
                        type="checkbox"
                        checked={formData.isMember}
                        onChange={(e) => handleInputChange('isMember', e.target.checked)}
                        className="w-5 h-5 text-blue-500 bg-white/10 border-white/20 rounded focus:ring-blue-500 focus:ring-2"
                      />
                      <Users className="w-5 h-5 text-blue-300" />
                      <span className="text-white font-medium">Are you a member of Aeronauts Society?</span>
                    </label>
                  </div>

                  {formData.isMember && (
                    <>
                      <div>
                        <label className="block text-sm font-medium mb-3 text-blue-200 flex items-center">
                          <Settings className="w-4 h-4 mr-2" />
                          Department
                        </label>
                        <select
                          value={formData.department}
                          onChange={(e) => handleInputChange('department', e.target.value)}
                          className="w-full p-3 sm:p-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl focus:border-blue-400 focus:outline-none text-white transition-all duration-300 text-sm sm:text-base"
                        >
                          <option value="" className="bg-gray-800">Select Department</option>
                          <option value="Technical" className="bg-gray-800">Technical</option>
                          <option value="Outreach" className="bg-gray-800">Outreach</option>
                          <option value="Automation" className="bg-gray-800">Automation</option>
                          <option value="Execution" className="bg-gray-800">Execution</option>
                          <option value="RnD" className="bg-gray-800">RnD</option>
                          <option value="DSA" className="bg-gray-800">DSA</option>
                          <option value="Others" className="bg-gray-800">Others</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-3 text-blue-200 flex items-center">
                          <Award className="w-4 h-4 mr-2" />
                          Role
                        </label>
                        <select
                          value={formData.role}
                          onChange={(e) => handleInputChange('role', e.target.value)}
                          className="w-full p-3 sm:p-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl focus:border-blue-400 focus:outline-none text-white transition-all duration-300 text-sm sm:text-base"
                        >
                          <option value="" className="bg-gray-800">Select Role</option>
                          <option value="Member" className="bg-gray-800">Member</option>
                          <option value="HOD" className="bg-gray-800">HOD</option>
                          <option value="Vice President" className="bg-gray-800">Vice President</option>
                          <option value="President" className="bg-gray-800">President</option>
                        </select>
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium mb-3 text-blue-200 flex items-center">
                          <User className="w-4 h-4 mr-2" />
                          Member ID
                        </label>
                        <input
                          type="text"
                          value={formData.memberId}
                          onChange={(e) => handleInputChange('memberId', e.target.value)}
                          placeholder="M001"
                          className="w-full p-3 sm:p-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl focus:border-blue-400 focus:outline-none text-white placeholder-blue-200/50 transition-all duration-300 text-sm sm:text-base"
                        />
                      </div>
                    </>
                  )}
                </div>

                <div className="mt-8">
                  <button
                    onClick={handleUpdateProfile}
                    className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-medium py-4 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl backdrop-blur-sm border border-blue-400/30 flex items-center justify-center space-x-3"
                  >
                    <Save className="w-5 h-5" />
                    <span>Update Profile</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* QR Code View */}
          {activeTab === "qr" && (
            <div className="max-w-2xl mx-auto">
              <div className="flex items-center space-x-2 sm:space-x-3 mb-6 sm:mb-8">
                <QrCode className="w-6 h-6 sm:w-8 sm:h-8 text-blue-400" />
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  Profile QR Code
                </h1>
              </div>

              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4 sm:p-6 lg:p-8 text-center shadow-2xl">
                <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-2xl inline-block mb-4 sm:mb-6 shadow-2xl">
                  <QRCode
                    value={getQRCodeData()}
                    size={256}
                    level="M"
                    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                  />
                </div>
                <div className="space-y-3 sm:space-y-4">
                  <p className="text-blue-100/90 text-sm sm:text-base lg:text-lg">
                    This QR code contains your profile information. It updates as you modify your details.
                  </p>
                  <div className="flex items-center justify-center space-x-2 text-blue-200/70">
                    <QrCode className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="text-xs sm:text-sm">Scan to view profile instantly</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


export default NewDashboard;